import { pluralFor } from '../plural';
import type { Messages } from './index';

const p = pluralFor('hr');

/**
 * Croatian.
 *
 * <p>No dual, but a separate *few* that English has no equivalent for: 2–4 takes one
 * form, 5+ another, and so do 22–24 against 25+ — the rule is on the last digit, not on
 * the magnitude, so "22 vozila" and "25 vozila" are different words. `Intl.PluralRules`
 * knows this; a hand-rolled `count === 1` does not, and neither does anyone porting the
 * Slovenian file across.
 *
 * <p>Kept apart from Slovenian on purpose — see the note in `sl.ts`. The vocabulary that
 * differs is exactly the vocabulary this product uses most.
 */
export const hr: Messages = {
  nav: {
    operate: 'Upravljanje',
    library: 'Vozni park',
    organization: 'Organizacija',
    fleetMap: 'Karta',
    dashboard: 'Nadzorna ploča',
    trackers: 'Pratioci',
    assets: 'Sredstva',
    trips: 'Vožnje',
    routes: 'Rute',
    geofences: 'Geoograde',
    alerts: 'Obavijesti',
    users: 'Korisnici',
    settings: 'Postavke',
    collapse: 'Sažmi izbornik',
    expand: 'Proširi izbornik',
    main: 'Glavni',
    openMenu: 'Otvori izbornik',
    platform: 'Platforma',
    platformOrganizations: 'Organizacije',
    platformPlans: 'Paketi',
    platformAdmins: 'Administratori platforme',
  },

  common: {
    language: 'Jezik',
    theme: 'Tema',
    light: 'Svijetla',
    dark: 'Tamna',
    system: 'Sistemska',
    signOut: 'Odjava',
    cancel: 'Odustani',
    save: 'Spremi',
    saving: 'Spremam…',
    retry: 'Pokušaj ponovno',
    close: 'Zatvori',
    search: 'Pretraga',
    clearFilter: 'Očisti filtar',
    justNow: 'upravo sada',
    never: 'Nikada',
    none: '—',
    unknown: 'Nepoznato',
    actions: 'Radnje',
    forbidden: 'Nemate pristup ovoj stranici',
    forbiddenBody: (role: string) =>
      `Vaša uloga je ${role}. Ako trebate više pristupa, obratite se administratoru.`,
    notFound: 'Stranica nije pronađena',
    notFoundBody: 'Ova ruta ne postoji.',
    previous: 'Natrag',
    next: 'Naprijed',
    edit: (what: string) => `Uredi ${what}`,
    archive: (what: string) => `Arhiviraj ${what}`,
    archiveConfirm: (what: string) => `Arhivirati ${what}? Povijest se čuva.`,
    pageOf: (page: string, total: string) => `Stranica ${page} od ${total}`,
  },

  role: {
    OWNER: 'Vlasnik',
    ADMIN: 'Administrator',
    DISPATCHER: 'Dispečer',
    VIEWER: 'Gledatelj',
  },

  status: {
    moving: 'U vožnji',
    idle: 'Miruje',
    stale: 'Zastarjelo',
    offline: 'Nedostupan',
    unreported: 'Još bez lokacije',
    alert: 'Upozorenje',
    lowBattery: 'Slaba baterija',
  },

  age: {
    justNow: 'upravo sada',
    minutes: (n: string) => `prije ${n} min`,
    hours: (h: string, m: number) => (m === 0 ? `prije ${h} h` : `prije ${h} h ${m} min`),
    yesterday: 'jučer',
    days: (n: number) =>
      p(n, {
        one: 'prije {n} dan',
        few: 'prije {n} dana',
        other: 'prije {n} dana',
      }),
    inMinutes: (n: string) => `za ${n} min`,
    inHours: (h: string, m: number) => (m === 0 ? `za ${h} h` : `za ${h} h ${m} min`),
    tomorrow: 'sutra',
    inDays: (n: number) =>
      p(n, {
        one: 'za {n} dan',
        few: 'za {n} dana',
        other: 'za {n} dana',
      }),
  },

  fleet: {
    title: 'Karta voznog parka',
    listTitle: 'Vozni park',
    listRegion: 'Popis voznog parka',
    showList: 'Prikaži popis',
    hideList: 'Sakrij popis',
    searchPlaceholder: 'Naziv ili ID uređaja',
    legend: 'Legenda',
    noMatches: 'Nema rezultata',
    noMatchesBody: 'Nijedan pratilac ne odgovara trenutnom filtru.',
    noPositions: 'Još nema lokacija',
    noTrackers: 'Nema pratilaca',
    noPositionsBody: 'Registrirani pratioci pojavit će se ovdje kada jave lokaciju.',
    noTrackersBody: 'Registrirajte pratioca da bi se prikazao na karti.',
    loadFailed: 'Vozni park nije bilo moguće učitati.',

    unreportedCount: (n: number) =>
      p(n, {
        one: 'Registriran je {n} pratilac koji još nije javio lokaciju.',
        few: 'Registrirana su {n} pratioca, nijedan još nije javio lokaciju.',
        other: 'Registrirano je {n} pratilaca, nijedan još nije javio lokaciju.',
      }),

    currentTrip: 'Vožnja na karti',
    tripSince: (time: string) => `od ${time}`,
    noRecentTrip: 'U zadnja 24 sata nema vožnje',
    fitAll: 'Prikaži sve pratioce',

    detail: {
      speed: 'Brzina',
      heading: 'Smjer',
      battery: 'Baterija',
      satellites: 'Sateliti',
      position: 'Lokacija',
      recordedByDevice: 'Zabilježio uređaj',
      receivedByServer: 'Primio poslužitelj',
      deviceClock: 'Sat uređaja',
      deviceClockUnset: 'Nije postavljen — vrijeme je poslužiteljevo',
      deviceId: 'ID uređaja',
      openAlert: 'Ovaj pratilac ima otvorenu obavijest.',
      historical:
        'Ovo je zadnja javljena lokacija, ne trenutna. Osjenčani krug na karti pokazuje koliko je daleko vozilo od tada moglo stići.',
      approximate: (metres: string) =>
        `Približna lokacija prema obližnjim baznim stanicama (± ${metres} m) — bez satelitskog signala. Vozilo je negdje unutar osjenčanog kruga.`,
      heldOnGnss: (age: string) =>
        `Trenutno bez satelitskog signala — procjena baznih stanica još se slaže sa zadnjom GPS lokacijom (${age}), pa je prikazana tamo.`,
    },
    tripLoading: 'Učitavanje rute …',

    lastFix: (age: string) => `zadnja lokacija ${age}`,
    noFixYet: 'još bez lokacije',
    battery: (percent: string) => `baterija ${percent}`,
    speed: (kmh: string) => `${kmh} km/h`,

    stacked: (n: number) =>
      p(n, {
        one: 'Ovdje je {n} pratilac, pritisnite za razmicanje',
        few: 'Ovdje su {n} pratioca, pritisnite za razmicanje',
        other: 'Ovdje je {n} pratilaca, pritisnite za razmicanje',
      }),

    filterLabel: 'Filtriraj vozni park',
    shownOfTotal: (shown: string, total: string) => `Prikazano ${shown} od ${total}`,

    matching: (n: number) =>
      p(n, {
        one: '{n} rezultat',
        few: '{n} rezultata',
        other: '{n} rezultata',
      }),
  },

  trips: {
    title: 'Vožnje',
    description: 'Vožnje rekonstruirane iz javljenih lokacija.',
    tracker: 'Pratilac',
    chooseTracker: 'Odaberite pratioca',
    chooseTrackerPlaceholder: 'Odaberite pratioca …',
    chooseTrackerBody: 'Vožnje se rekonstruiraju po pratiocu iz njegovih javljenih lokacija.',
    from: 'Od',
    to: 'Do',
    back: 'Vožnje',
    backLabel: 'Natrag na popis vožnji',
    noTrips: 'U ovom razdoblju nema vožnji',
    noTripsBody: 'Pratilac je ili mirovao ili tih dana nije ništa javio.',
    loadFailed: 'Vožnje nije bilo moguće učitati.',
    noneSelected: 'Nijedna vožnja nije odabrana',
    noneSelectedBody: 'Odaberite vožnju s lijeve strane za reprodukciju.',
    back1: 'Jedna lokacija natrag',
    forward1: 'Jedna lokacija naprijed',
    play: 'Pokreni',
    pause: 'Pauza',
    scrubberLabel: 'Položaj u vožnji, po lokacijama',
    fixOf: (index: string, total: string) => `${index} / ${total}`,

    distance: (km: string) => `${km} km između lokacija`,

    fixes: (n: number) =>
      p(n, {
        one: '{n} lokacija',
        few: '{n} lokacije',
        other: '{n} lokacija',
      }),

    duration: (hours: number, minutes: number) =>
      hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`,

    maxSpeed: (kmh: string) => `najv. ${kmh} km/h`,

    filteredSegments: (n: number) =>
      p(n, {
        one: '{n} nevjerojatan skok nije uračunat u udaljenost',
        few: '{n} nevjerojatna skoka nisu uračunata u udaljenost',
        other: '{n} nevjerojatnih skokova nije uračunato u udaljenost',
      }),
  },

  trackers: {
    title: 'Pratioci',
    description: 'Uređaji za praćenje registrirani u vašoj organizaciji.',
    register: 'Registriraj',
    searchPlaceholder: 'Naziv, ID uređaja ili sredstvo',
    filterLabel: 'Filtriraj pratioce na ovoj stranici',
    tableCaption: 'Registrirani pratioci',
    colTracker: 'Pratilac',
    colAsset: 'Sredstvo',
    colLastSeen: 'Zadnji put viđen',
    colBattery: 'Baterija',
    colInterval: 'Interval',
    colAlerts: 'Obavijesti',
    unassigned: 'Nedodijeljen',
    noMatches: 'Nema rezultata',
    noMatchesBody: 'Nijedan pratilac na ovoj stranici ne odgovara filtru.',
    empty: 'Još nema pratilaca',
    emptyBody: 'Registrirajte ID uređaja kako biste počeli primati njegovu telemetriju.',
    loadFailed: 'Pratioce nije bilo moguće učitati.',
    minutes: (n: string) => `${n} min`,
    count: (n: number) =>
      p(n, { one: '{n} pratilac', few: '{n} pratioca', other: '{n} pratilaca' }),

    form: {
      name: 'Naziv',
      nameHint: 'Ovo će dispečer tražiti.',
      deviceId: 'ID uređaja',
      interval: 'Interval javljanja',
      intervalHint: 'Sekunde između lokacija. Ostavite prazno za zadanu vrijednost organizacije.',
      intervalElsewhere:
        'Interval javljanja i ostale postavke uređaja nalaze se pod Konfiguracija uređaja.',
      deviceIdHint: 'Točno ono što uređaj objavljuje na trackers/<device-id>/telemetry.',
      editTitle: 'Uredi pratioca',
      createTitle: 'Registriraj pratioca',
      editHint: 'ID uređaja se nakon preuzimanja ne mijenja.',
      createHint:
        'Preuzmite ID uređaja u ovu organizaciju kako bi njegova telemetrija bila prihvaćena.',
    },
  },

  trackerConfig: {
    open: (name: string) => `Konfiguracija uređaja za ${name}`,
    title: 'Konfiguracija uređaja',
    description: (name: string) =>
      `Kako ${name} javlja lokaciju, traži satelitski signal i štedi energiju.`,
    readOnly: 'Konfiguraciju uređaja može promijeniti samo administrator.',
    loadFailed: 'Konfiguraciju uređaja nije bilo moguće učitati.',
    saveFailed: 'Konfiguraciju uređaja nije bilo moguće spremiti.',
    saved: 'Spremljeno.',
    override: 'Zasebna vrijednost',
    overrideLabel: (field: string) => `Zasebna vrijednost: ${field}`,
    inheritedLabel: 'Zadano za organizaciju',
    inherited: (value: string) => `Zadano za organizaciju: ${value}`,
    statusApplied: 'Primijenjeno',
    statusPending: 'Na čekanju',
    appliedHint: 'Pratilac je potvrdio da radi s ovom konfiguracijom.',
    pendingHint:
      'Primjenjuje se pri sljedećem spajanju pratioca (u načinu spavanja: unutar jednog intervala javljanja).',
    revision: (n: string) => `Revizija ${n}`,

    fields: {
      mode: {
        label: 'Način napajanja',
        hint: 'Spavanje za pratioce na bateriju, stalno uključeno za pratioce spojene na vozilo.',
      },
      reportIntervalSec: {
        label: 'Interval javljanja',
        hint: 'Vrijeme između lokacija. U načinu spavanja najmanje 1 min.',
      },
      gnssTimeoutColdSec: {
        label: 'GNSS vremensko ograničenje, hladni start',
        hint: 'Koliko dugo tražiti lokaciju nakon uključivanja ili dugog spavanja prije odustajanja.',
      },
      gnssTimeoutWarmSec: {
        label: 'GNSS vremensko ograničenje, topli start',
        hint: 'Koliko dugo tražiti lokaciju kada je nedavna lokacija dostupna.',
      },
      gnssMaxHdop: {
        label: 'Najveći HDOP',
        hint: 'Lokacije manje preciznosti od ove se odbacuju. Manja vrijednost je stroža.',
      },
      lowBatteryCutoffMv: {
        label: 'Isključenje pri slaboj bateriji',
        hint: 'Ispod ovog napona pratilac prestaje javljati kako bi zaštitio bateriju.',
      },
      lowBatterySleepSec: {
        label: 'Spavanje pri slaboj bateriji',
        hint: 'Koliko dugo pratilac spava prije ponovne provjere baterije.',
      },
      uploadEveryFixes: {
        label: 'Slanje svakih N pozicija',
        hint: 'Način spavanja: pozicije se spremaju i šalju pri svakom N-tom buđenju. Više = dulje trajanje baterije, veće kašnjenje na karti.',
      },
    },

    mode: {
      SLEEP: 'Spavanje (baterija)',
      ALWAYS_ON: 'Stalno uključen (napajanje vozila)',
    },
    modeHint: {
      SLEEP: 'Probudi se, javi lokaciju, ponovno zaspi.',
      ALWAYS_ON: 'Ostaje stalno povezan.',
    },

    units: {
      seconds: (n: string) => `${n} s`,
      minutes: (n: string) => `${n} min`,
      hours: (n: string) => `${n} h`,
      millivolts: (n: string) => `${n} mV`,
      /** Beside an input that already holds the number. */
      secondsSymbol: 's',
      millivoltsSymbol: 'mV',
    },

    validation: {
      range: (min: string, max: string) => `Unesite vrijednost od ${min} do ${max}.`,
      wholeNumber: 'Unesite cijeli broj.',
      sleepInterval: 'Način spavanja zahtijeva interval javljanja od najmanje 1 min.',
    },

    errors: {
      defaultsIncomplete: 'Svaka zadana vrijednost mora biti postavljena.',
      defaultsConflict:
        'Neki pratilac ima zaseban interval javljanja kraći od 1 min i završio bi u načinu spavanja. Najprije promijenite tog pratioca.',
    },

    defaults: {
      title: 'Zadane postavke pratilaca',
      description:
        'Svi pratioci koriste ove vrijednosti osim ako u vlastitoj konfiguraciji uređaja ne postave zasebnu. Promjene stižu na uređaj pri njegovom sljedećem spajanju.',
      adminOnly: 'Zadane postavke pratilaca može promijeniti samo administrator.',
      loadFailed: 'Zadane postavke pratilaca nije bilo moguće učitati.',
    },
  },

  assets: {
    title: 'Sredstva',
    description: 'Vozila i pošiljke na koje su pričvršćeni vaši pratioci.',
    create: 'Novo sredstvo',
    searchPlaceholder: 'Naziv, oznaka ili pratilac',
    filterLabel: 'Filtriraj sredstva na ovoj stranici',
    tableCaption: 'Sredstva',
    colAsset: 'Sredstvo',
    colKind: 'Vrsta',
    editTitle: 'Uredi sredstvo',

    kind: {
      TRUCK: 'Kamion',
      TRAILER: 'Prikolica',
      CONTAINER: 'Kontejner',
      SHIPMENT: 'Pošiljka',
      OTHER: 'Ostalo',
    },
    colTracker: 'Pratilac',
    loadFailed: 'Sredstva nije bilo moguće učitati.',

    form: {
      name: 'Naziv',
      kind: 'Vrsta',
      reference: 'Oznaka',
      referenceHint: 'Registracija, broj pošiljke ili ERP ID. Mora biti jedinstvena.',
      notes: 'Bilješke',
      hint: 'Vozilo, prikolica ili pošiljka na koju je pričvršćen pratilac.',
    },

    attach: {
      title: 'Pričvrsti pratioca',
      placeholder: 'Odaberite pratioca …',
      none: 'Nema slobodnih pratilaca',
      noneBody: 'Svaki registrirani pratilac već je pričvršćen na neko sredstvo.',
    },
  },

  alerts: {
    title: 'Obavijesti',
    description: 'Nedostupni uređaji, slabe baterije i prelasci geoograda.',
    tabOpen: 'Otvorene',
    tabAcknowledged: 'Potvrđene',
    tabAll: 'Sve',
    acknowledge: 'Vidio sam',
    acknowledgeAllCount: (n: number) =>
      p(n, { one: 'Potvrdi {n}', few: 'Potvrdi {n}', other: 'Potvrdi {n}' }),
    acknowledgeShort: 'Potvrdi',
    resolve: 'Zatvori',
    resolved: 'Zatvoreno',
    severityCritical: 'Kritično',
    severityWarning: 'Upozorenje',
    severityInfo: 'Informacija',
    allClear: 'Sve je u redu',
    allClearBody: 'Nema otvorenih obavijesti. Svi pratioci javljaju kako se očekuje.',
    nothingHere: 'Ovdje nema ničega',
    loadFailed: 'Obavijesti nije bilo moguće učitati.',
  },

  dashboard: {
    title: 'Nadzorna ploča',
    description: 'Stanje voznog parka na prvi pogled.',
    updated: 'Ažurirano',
    reporting: 'Javlja',
    openAlerts: 'Otvorene obavijesti',
    fixesThisHour: 'Lokacije u ovom satu',
    assets: 'Sredstva',
    trackerStatus: 'Stanje pratilaca',
    battery: 'Baterija',
    online: 'Povezan',
    stale: 'Zastarjelo',
    offline: 'Nedostupan',
    disabled: 'Onemogućen',
    neverReported: 'Nikada nije javio',
    critical: 'Kritično',
    notReported: 'Nema podatka',
    loadFailed: 'Nadzornu ploču nije bilo moguće učitati.',
    low: 'Slaba',

    someOffline: (n: number) =>
      p(n, { one: '{n} nedostupan', few: '{n} nedostupna', other: '{n} nedostupnih' }),
    allReporting: 'Svi pratioci javljaju',
    someCritical: (n: number) =>
      p(n, { one: '{n} kritično', few: '{n} kritična', other: '{n} kritičnih' }),
    raisedToday: (n: number) =>
      p(n, {
        one: '{n} pokrenuta danas',
        few: '{n} pokrenute danas',
        other: '{n} pokrenutih danas',
      }),
    inLast24h: (n: string) => `${n} u 24 h`,
    withoutTracker: (n: number) =>
      p(n, { one: '{n} bez pratioca', few: '{n} bez pratioca', other: '{n} bez pratioca' }),
    allTracked: 'Sva sredstva se prate',
  },

  users: {
    title: 'Korisnici',
    description: 'Tko se može prijaviti i što smije raditi.',
    tableCaption: 'Članovi organizacije',
    colMember: 'Član',
    colRole: 'Uloga',
    colStatus: 'Status',
    colLastSeen: 'Zadnji put viđen',
    you: '(vi)',
    active: 'Aktivan',
    deactivated: 'Deaktiviran',
    empty: 'Nema članova',
    emptyBody: 'Pozovite nekoga za početak.',
    loadFailed: 'Članove nije bilo moguće učitati.',

    invitations: 'Pozivnice',
    invitationsHint: 'Bez pozivnice prijava nije moguća, pa novi korisnici pristup dobivaju tako.',
    inviteEmailLabel: 'E-mail adresa za poziv',
    inviteEmailPlaceholder: 'ime@tvrtka.hr',
    invite: 'Pozovi',
    revoke: 'Opozovi pozivnicu',
    expires: 'istječe',
  },

  routes: {
    title: 'Rute',
    description: 'Planirane vožnje i koridori izgrađeni iz njih.',
    new: 'Nova ruta',
    empty: 'Još nema ruta',
    emptyBody:
      'Isplanirajte vožnju s početnom i završnom adresom, zatim od nje napravite koridor koji upozorava kad vozilo skrene s rute.',
    loadFailed: 'Rute nije bilo moguće učitati.',
    name: 'Naziv',
    namePlaceholder: 'Ljubljana → Koper',
    vehicle: 'Vozilo',
    chooseVehicle: 'Odaberite vozilo…',
    noVehicle: 'Vozilo još nije određeno',
    start: 'Početak',
    end: 'Odredište',
    stop: 'Stajalište',
    addStop: 'Dodaj stajalište',
    clearStart: 'Očisti početak',
    clearEnd: 'Očisti odredište',
    unresolvedStart: 'Početak još nema adresu.',
    unresolvedEnd: 'Odredište još nema adresu.',
    removeStop: (n: number) => `Ukloni stajalište ${n}`,
    moveStopUp: (n: number) => `Pomakni stajalište ${n} ranije`,
    moveStopDown: (n: number) => `Pomakni stajalište ${n} kasnije`,
    addressPlaceholder: 'Adresa ili klik na kartu',
    computing: 'Traženje ceste …',
    computeFailed: 'Rutu nije bilo moguće izračunati.',
    noRoute: 'Između ovih stajališta nema vozne rute.',
    stopNotOnRoad: 'Jedno stajalište nije blizu ceste. Pomaknite ga bliže.',
    tooLong: 'Ova je vožnja predugačka za jednu rutu. Podijelite je na stajalištu.',
    unresolvedStop: (n: number) => `Stajalište ${n} još nema adresu.`,
    snappedFar: (metres: string) => `${metres} m od najbliže ceste`,
    distance: (km: string) => `${km} km`,
    duration: (hours: number, minutes: number) =>
      hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`,
    stops: (n: number) =>
      p(n, { one: '{n} stajalište', few: '{n} stajališta', other: '{n} stajališta' }),
    createCorridor: 'Napravi koridor',
    hasCorridor: 'Koridor napravljen',
    corridorCreated: 'Koridor je napravljen iz ove rute.',
    corridorExists: 'Iz ove rute koridor je već napravljen.',
    needsVehicle: 'Prije izrade koridora odredite vozilo.',
    accuracyNote: (metres: string) =>
      `Geometrija ove rute točna je na otprilike ${metres} m, pa je stvarna tolerancija koridora za toliko manja.`,
    toleranceTooTight: 'Preusko za geometriju ove rute. Proširite je.',
    corridorPoints: (n: number) =>
      p(n, {
        one: 'Koridor prati {n} točku',
        few: 'Koridor prati {n} točke',
        other: 'Koridor prati {n} točaka',
      }),
    deleteConfirm: (name: string) => `Arhivirati ${name}? Napravljeni koridor ostaje.`,
    saveFailed: 'Rutu nije bilo moguće spremiti.',
    useThisRoute: 'Upotrijebi ovu rutu',
    planRoute: 'Planiraj iz adresa',
  },

  address: {
    label: 'Adresa',
    placeholder: 'Potražite adresu',
    searching: 'Pretraživanje …',
    noResults: 'Nema odgovarajuće adrese',
    typeMore: (n: number) =>
      p(n, {
        one: 'Unesite još {n} znak',
        few: 'Unesite još {n} znaka',
        other: 'Unesite još {n} znakova',
      }),
    unavailable: 'Pretraga adresa nije dostupna. Kliknite na kartu za stajalište.',
    approximate: 'Približno',
  },

  geofences: {
    title: 'Geoograde',
    show: 'Prikaži geoograde',
    hide: 'Sakrij geoograde',
    newArea: 'Novo područje',
    newCorridor: 'Novi koridor rute',
    empty: 'Nema geoograda',
    emptyBody:
      'Nacrtajte područje oko lokacije ili koridor rute kako biste bili obaviješteni kada vozilo napusti planirani put.',
    loadFailed: 'Geoograde nije bilo moguće učitati.',
    placeCentre: 'Kliknite na kartu za postavljanje središta.',
    placeRoute: 'Klikajte uzduž rute za dodavanje točaka.',
    escToCancel: 'za odustajanje.',

    editor: {
      name: 'Naziv',
      namePlaceholder: 'Skladište Zagreb',
      radius: (metres: string) => `Polumjer · ${metres} m`,
      tolerance: (metres: string) => `Odstupanje · ${metres} m`,
      radiusHint: 'Mjereno na terenu, ne u pikselima.',
      appliesTo: 'Vrijedi za',
      everything: 'Sve pratioce',
      undoPoint: 'Poništi zadnju točku',
      notifyEnter: 'Obavijesti pri dolasku',
      notifyExit: 'Obavijesti pri odlasku',
      notifyRejoin: 'Obavijesti kada se vrati na rutu',
      notifyDeviate: 'Obavijesti kada napusti rutu',
      baselineNote: (kind: string) =>
        `Pratioci koji su pri nastanku već ${kind} bilježe se bez obavijesti.`,
      routeHintNeedMore: 'Kliknite na kartu za dodavanje točaka. Ruta treba barem dvije.',
      routeHintExtend: 'Kliknite za produženje rute.',
      toleranceHint: 'Koliko daleko od rute još znači na ruti. Uzmite u obzir GPS pogrešku.',
      scopeAssetHint: 'Prati sredstvo, pa vrijedi i kad se pratilac zamijeni.',
      baselineInside: 'unutar',
      baselineOffRoute: 'izvan rute',
      deleteConfirm: (name: string) => `Obrisati ${name}? Prošli događaji se čuvaju.`,
      pause: (name: string) => `Pauziraj ${name}`,
      resume: (name: string) => `Nastavi ${name}`,
      delete: (name: string) => `Obriši ${name}`,
      paused: 'pauzirano',
      saveFailed: 'Geoogradu nije bilo moguće spremiti.',
    },
  },

  settings: {
    title: 'Postavke',
    description: 'Pragovi, izgled i vaš račun.',
    tabOrganization: 'Organizacija',
    tabTrackerDefaults: 'Zadane postavke uređaja',
    tabAppearance: 'Izgled',
    tabProfile: 'Profil',
    name: 'Naziv',
    timeZone: 'Vremenska zona',
    timeZoneHint: 'IANA ID zone, na primjer Europe/Zagreb.',
    offlineAfter: 'Nedostupan nakon',
    offlineAfterHint: (duration: string) =>
      `Propuštenih prozora javljanja. Pri trenutnom intervalu to je oko ${duration} tišine.`,
    lowBattery: 'Prag slabe baterije',
    lowBatteryHint: 'Milivolti, ne postotak: uređaj javlja napon.',
    ownerOnly:
      'Ovo može promijeniti samo vlasnik. Zajedno određuju kada se pratilac smatra nedostupnim.',
    saveFailed: 'Postavke nije bilo moguće spremiti.',
    loadFailed: 'Postavke organizacije nije bilo moguće učitati.',
  },

  account: {
    signingIn: 'Prijava u tijeku',
    switching: 'Mijenjam organizaciju',
    loadFailed: 'Vaš račun nije bilo moguće učitati',
    loadFailedBody:
      'Prijava je uspjela, ali aplikacija nije mogla dohvatiti vaš profil. To obično prođe samo od sebe.',
    notInvited: 'Još niste pozvani',
    notInvitedBody: 'Zamolite administratora da pozove ovu e-mail adresu.',
    noOrganization: 'Još nemate organizaciju',
    noOrganizationBody:
      'Vaš račun još nije dio nijedne organizacije. Zamolite administratora za pozivnicu ili nam se javite u vezi s paketom.',
    suspended: 'Ova organizacija je suspendirana',
    suspendedBody:
      'Njezini podaci nisu dostupni dok je administrator platforme ponovno ne aktivira. Obratite mu se ako mislite da je riječ o pogrešci.',
    continueElsewhere: 'Nastavi u drugoj organizaciji',
  },

  switcher: {
    label: 'Promijeni organizaciju',
    heading: 'Vaše organizacije',
    current: (name: string) => `Trenutna organizacija: ${name}`,
    defaultBadge: 'Zadana',
    setDefault: (name: string) => `Postavi ${name} kao zadanu`,
    isDefault: (name: string) => `${name} je vaša zadana organizacija`,
    platformAccess: 'Pristup platforme',
    allOrganizations: 'Sve organizacije…',
    setDefaultFailed: 'Zadanu organizaciju nije bilo moguće promijeniti.',
  },

  limits: {
    unlimited: 'Neograničeno',
    seats: (used: string, limit: string) => `Korisnici: ${used} / ${limit}`,
    trackers: (used: string, limit: string) => `Pratioci: ${used} / ${limit}`,
    usage: (used: string, limit: string) => `${used} / ${limit}`,
    seatLimitReached:
      'Sva korisnička mjesta u ovoj organizaciji su popunjena, uključujući pozivnice na čekanju. Najprije uklonite člana ili opozovite pozivnicu ili se raspitajte o većem paketu.',
    trackerLimitReached:
      'Organizacija je dosegla ograničenje broja pratilaca. Najprije arhivirajte pratilac ili se raspitajte o većem paketu.',
  },

  platform: {
    status: { ACTIVE: 'Aktivna', SUSPENDED: 'Suspendirana' },
    billingPeriod: { MONTHLY: 'Mjesečno', YEARLY: 'Godišnje' },
    perMonth: (price: string) => `${price} / mjesec`,
    perYear: (price: string) => `${price} / godina`,
    noPlan: 'Bez paketa',
    customLimits: 'Vlastita ograničenja',
    unnamed: 'Bez imena',
    you: '(vi)',
    switchInto: 'Prebaci se',
    switchIntoNamed: (name: string) => `Prebaci se u ${name}`,
    open: (name: string) => `Otvori ${name}`,

    orgs: {
      title: 'Organizacije',
      description: 'Svi korisnici platforme, njihov paket i koliko ga troše.',
      create: 'Nova organizacija',
      searchPlaceholder: 'Naziv ili oznaka',
      searchLabel: 'Pretraži organizacije',
      tableCaption: 'Organizacije',
      colOrganization: 'Organizacija',
      colPlan: 'Paket',
      colStatus: 'Status',
      colSeats: 'Korisnici',
      colTrackers: 'Pratioci',
      colCreated: 'Stvorena',
      empty: 'Još nema organizacija',
      emptyBody: 'Stvorite prvu i pozovite njezina vlasnika.',
      noMatches: 'Nema rezultata',
      noMatchesBody: 'Nijedna organizacija ne odgovara pretrazi.',
      loadFailed: 'Organizacije nije bilo moguće učitati.',
      count: (n: number) =>
        p(n, { one: '{n} organizacija', few: '{n} organizacije', other: '{n} organizacija' }),
    },

    orgForm: {
      createTitle: 'Nova organizacija',
      createHint: 'Stvara organizaciju i šalje pozivnice. Barem jedan pozvani mora biti vlasnik.',
      name: 'Naziv',
      slug: 'Oznaka',
      slugHint: 'Mala slova, znamenke i -. Ostavite prazno da se izvede iz naziva.',
      slugInvalid: 'Samo mala slova, znamenke i -, na početku slovo ili znamenka.',
      slugTaken: 'Ta oznaka je već zauzeta.',
      plan: 'Paket',
      planLimitsHint: 'Ograničenja određuje paket.',
      seatLimit: 'Ograničenje korisnika',
      trackerLimit: 'Ograničenje pratilaca',
      unlimited: 'Neograničeno',
      limitInvalid: 'Unesite cijeli broj jednak ili veći od nule.',
      prefilled:
        'Popunjeno iz paketa. Promijenite kako biste organizaciji dali vlastita ograničenja.',
      invites: 'Pozivnice',
      invitesHint: 'Pridružuju se kad se prijave s ovom adresom.',
      inviteEmail: (n: number) => `E-mail pozvanog ${n}`,
      inviteRole: (n: number) => `Uloga pozvanog ${n}`,
      addInvite: 'Dodaj pozvanog',
      removeInvite: (n: number) => `Ukloni pozvanog ${n}`,
      ownerRequired: 'Barem jedan pozvani mora biti vlasnik.',
      seatsTooFew: 'Odabrana ograničenja imaju manje korisničkih mjesta nego pozivnica.',
      submit: 'Stvori organizaciju',
      createFailed: 'Organizaciju nije bilo moguće stvoriti.',
    },

    orgDetail: {
      back: 'Organizacije',
      backLabel: 'Natrag na sve organizacije',
      loadFailed: 'Ovu organizaciju nije bilo moguće učitati.',
      created: (date: string) => `Stvorena ${date}`,
      settings: 'Postavke',
      notes: 'Bilješke',
      notesHint: 'Vide ih samo administratori platforme.',
      status: 'Status',
      saved: 'Spremljeno.',
      saveFailed: 'Organizaciju nije bilo moguće spremiti.',
      usage: 'Potrošnja',
      seats: 'Korisnici',
      trackers: 'Pratioci',
      members: 'Članovi',
      membersCaption: 'Članovi ove organizacije',
      membersEmpty: 'Još nema članova',
      membersEmptyBody: 'Pojavljuju se kad prihvate pozivnicu.',
      memberCount: (n: number) => p(n, { one: '{n} član', few: '{n} člana', other: '{n} članova' }),
      invitations: 'Pozivnice',
      pendingCount: (n: number) =>
        p(n, {
          one: '{n} pozivnica na čekanju',
          few: '{n} pozivnice na čekanju',
          other: '{n} pozivnica na čekanju',
        }),
      noPending: 'Nema pozivnica na čekanju.',
      deactivate: (name: string) => `Deaktiviraj ${name}`,
      reactivate: (name: string) => `Ponovno aktiviraj ${name}`,
      lastOwner: 'Organizacija mora zadržati barem jednog aktivnog vlasnika.',
      inviteAlreadyPending: 'Ta adresa već ima pozivnicu na čekanju.',
      alreadyMember: 'Ta osoba je već član.',
      memberUpdateFailed: 'Člana nije bilo moguće ažurirati.',
      suspendTitle: (name: string) => `Suspendirati ${name}?`,
      suspendBody:
        'Njezini članovi gube pristup svim zaslonima i API-ju dok je ponovno ne aktivirate. Pratioci i dalje šalju podatke i ništa se ne briše.',
      suspendConfirm: 'Suspendiraj',
    },

    plans: {
      title: 'Paketi',
      description: 'Na što organizacija može biti pretplaćena i koja ograničenja to donosi.',
      create: 'Novi paket',
      tableCaption: 'Paketi',
      colPlan: 'Paket',
      colPrice: 'Cijena',
      colSeats: 'Korisnici',
      colTrackers: 'Pratioci',
      colOrganizations: 'Organizacije',
      colStatus: 'Status',
      active: 'Aktivan',
      inactive: 'Neaktivan',
      empty: 'Još nema paketa',
      emptyBody: 'Definirajte paket kako biste organizacijama dodijelili cijenu i ograničenja.',
      loadFailed: 'Pakete nije bilo moguće učitati.',
      edit: (name: string) => `Uredi ${name}`,
      usedBy: (n: number) =>
        p(n, { one: '{n} organizacija', few: '{n} organizacije', other: '{n} organizacija' }),
    },

    planForm: {
      createTitle: 'Novi paket',
      editTitle: 'Uredi paket',
      hint: 'Ograničenja vrijede za sve organizacije s ovim paketom.',
      code: 'Kod',
      codeHint: 'Stalni identifikator: mala slova, znamenke, - i _.',
      codeInvalid: 'Samo mala slova, znamenke, - i _, na početku slovo ili znamenka.',
      codeTaken: 'Taj kod je već zauzet.',
      name: 'Naziv',
      description: 'Opis',
      price: 'Cijena',
      priceHint: 'U valuti ispod, na primjer 49,00.',
      priceInvalid: 'Unesite iznos jednak ili veći od nule, s najviše dvije decimale.',
      currency: 'Valuta',
      currencyInvalid: 'Tri velika slova, na primjer EUR.',
      billingPeriod: 'Razdoblje naplate',
      maxSeats: 'Korisnici',
      maxTrackers: 'Pratioci',
      active: 'Dostupan',
      activeHint: 'Neaktivan paket ostaje na organizacijama koje ga već imaju.',
      saveFailed: 'Paket nije bilo moguće spremiti.',
    },

    admins: {
      title: 'Administratori platforme',
      description: 'Osobe koje mogu upravljati svim organizacijama, paketima i ograničenjima.',
      add: 'Dodaj administratora platforme',
      addHint: 'Mogu se pronaći samo osobe koje su se barem jednom prijavile.',
      tableCaption: 'Administratori platforme',
      colPerson: 'Osoba',
      colSince: 'Račun stvoren',
      revoke: (name: string) => `Oduzmi administratorska prava osobi ${name}`,
      revokeTitle: (name: string) => `Oduzeti prava osobi ${name}?`,
      revokeBody: 'Zadržava članstva u organizacijama, ali više ne može upravljati platformom.',
      revokeConfirm: 'Oduzmi',
      cannotRevokeSelf: 'Ne možete oduzeti vlastiti pristup platformi.',
      lastAdmin: 'Platforma mora imati barem jednog administratora.',
      actionFailed: 'Pristup platformi nije bilo moguće promijeniti.',
      loadFailed: 'Administratore platforme nije bilo moguće učitati.',
      count: (n: number) =>
        p(n, {
          one: '{n} administrator',
          few: '{n} administratora',
          other: '{n} administratora',
        }),
      searchLabel: 'Pretraži osobe koje su se prijavile',
      searchPlaceholder: 'Ime ili e-mail',
      noResults: 'Nitko ne odgovara pretrazi.',
      grant: 'Dodijeli',
      grantNamed: (name: string) => `Dodijeli administratorska prava osobi ${name}`,
      alreadyAdmin: 'Već administrator',
    },
  },

  validation: {
    required: 'Obavezno.',
    minChars: (n: number) =>
      p(n, {
        one: 'Najmanje {n} znak.',
        few: 'Najmanje {n} znaka.',
        other: 'Najmanje {n} znakova.',
      }),
    maxChars: (n: number) =>
      p(n, { one: 'Najviše {n} znak.', few: 'Najviše {n} znaka.', other: 'Najviše {n} znakova.' }),
    minSeconds: (n: number) =>
      p(n, {
        one: 'Najmanje {n} sekunda.',
        few: 'Najmanje {n} sekunde.',
        other: 'Najmanje {n} sekundi.',
      }),
    deviceIdChars: 'Samo slova, znamenke i . _ : -',
    email: 'Unesite valjanu e-mail adresu.',
  },
};
